import type { CodexMatchupForecast } from "@dailywars/core/engine";
import { MoveLeft, MoveRight, Skull } from "lucide-react";

import { CoinMoneyIcon } from "#/components/game/CoinMoneyIcon";
import { cn } from "#/lib/utils";

import { willKillFromCounter } from "./-codex.damageCalculator.fields";

function formatGold(value: number): string {
  return Math.round(value).toLocaleString();
}

function CodexCalculatorSectionLabel({ children }: { children: string }) {
  return (
    <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
      {children}
    </span>
  );
}

function CodexCalculatorDamageGold({
  value,
  goldValue,
  willKill,
}: {
  value: number;
  goldValue: number;
  willKill: boolean;
}) {
  return (
    <span className="inline-flex items-center justify-center gap-1 text-sm font-semibold whitespace-nowrap tabular-nums">
      {willKill ? <Skull aria-hidden className="size-3.5 shrink-0 text-red-300" /> : null}
      <span className={cn(willKill ? "text-red-300" : "text-foreground")}>{value}%</span>
      <span className="inline-flex items-center gap-0.5 font-normal text-muted-foreground">
        ({formatGold(goldValue)}
        <CoinMoneyIcon size="sm" className="!h-3 !w-3" />)
      </span>
    </span>
  );
}

function CodexCalculatorGoldTrade({ goldTrade }: { goldTrade: number }) {
  const positive = goldTrade > 0;
  const negative = goldTrade < 0;

  return (
    <div className="flex flex-col items-center gap-0.5 py-0.5">
      <CodexCalculatorSectionLabel>Gold trade</CodexCalculatorSectionLabel>
      <span
        className={cn(
          "inline-flex items-center gap-0.5 text-sm font-semibold tabular-nums",
          positive && "text-emerald-300",
          negative && "text-rose-300",
          !positive && !negative && "text-muted-foreground",
        )}
      >
        {positive ? "+" : ""}
        {formatGold(goldTrade)}
        <CoinMoneyIcon size="sm" className="!h-3 !w-3" />
      </span>
    </div>
  );
}

function CodexCalculatorDamageRow({
  label,
  value,
  goldValue,
  willKill,
}: {
  label: string;
  value: number;
  goldValue: number;
  willKill: boolean;
}) {
  return (
    <div className="flex flex-col items-center gap-0.5">
      <CodexCalculatorSectionLabel>{label}</CodexCalculatorSectionLabel>
      <CodexCalculatorDamageGold value={value} goldValue={goldValue} willKill={willKill} />
    </div>
  );
}

function CodexCalculatorCounterLine({
  forecast,
  willKillAttacker,
}: {
  forecast: CodexMatchupForecast;
  willKillAttacker: boolean;
}) {
  if (forecast.counterDamage !== null) {
    return (
      <CodexCalculatorDamageRow
        label="Counterattack"
        value={forecast.counterDamage}
        goldValue={forecast.counterGoldValue ?? 0}
        willKill={willKillAttacker}
      />
    );
  }

  return (
    <div className="flex flex-col items-center gap-0.5">
      <CodexCalculatorSectionLabel>Counterattack</CodexCalculatorSectionLabel>
      <span className="text-xs text-muted-foreground">No counter</span>
    </div>
  );
}

export function CodexCalculatorMatchupCenter({
  forecast,
  attackerDisplayHp,
  className,
}: {
  forecast: CodexMatchupForecast;
  attackerDisplayHp: number;
  className?: string;
}) {
  if (!forecast.canAttack) {
    return (
      <div className="flex min-h-16 min-w-20 items-center justify-center px-1 text-center text-xs text-muted-foreground">
        No damage
      </div>
    );
  }

  const willKillAttacker = willKillFromCounter({
    counterDamage: forecast.counterDamage,
    attackerDisplayHp,
  });

  return (
    <div
      className={cn(
        "flex min-w-36 flex-col items-center justify-center self-center px-2",
        className,
      )}
    >
      <div className="flex flex-col items-center gap-1.5">
        <CodexCalculatorDamageRow
          label="Attack"
          value={forecast.attackDamage}
          goldValue={forecast.attackGoldValue}
          willKill={forecast.defenderKilled}
        />
        <MoveRight aria-hidden className="size-5 text-muted-foreground" strokeWidth={2.5} />
        <CodexCalculatorGoldTrade goldTrade={forecast.goldTrade} />
        <MoveLeft aria-hidden className="size-5 text-muted-foreground" strokeWidth={2.5} />
        <CodexCalculatorCounterLine forecast={forecast} willKillAttacker={willKillAttacker} />
      </div>
    </div>
  );
}
